import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { ReactNode } from 'react'
import { SectionCard } from '@/components/common/SectionCard'

export type Fact = { icon?: ReactNode; label: ReactNode }

export function AvatarTile({ initials, avatarUrl, className }: { initials?: string; avatarUrl?: string; variant?: 'soft' | 'gradient'; className?: string }) {
  return (
    <div
      className={cn(
        'grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-2xl border border-primary/20 bg-accent text-base font-bold text-accent-foreground',
        className
      )}
    >
      {avatarUrl ? <img src={avatarUrl} alt={initials} className="h-full w-full object-cover" /> : initials}
    </div>
  )
}

export function FactPill({ icon, children }: { icon?: ReactNode; children: ReactNode; variant?: 'plain' | 'accent' }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-[12.5px] text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:shrink-0">
      {icon}
      <span className="truncate">{children}</span>
    </span>
  )
}

export function BannerShell({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('rounded-2xl border border-border bg-card px-5 py-4 shadow-xs', className)}>{children}</div>
}

export function ProgressMeter({ value }: { value: number }) {
  const v = Math.max(0, Math.min(100, Math.round(value)))
  return (
    <div className="flex items-center gap-3">
      <div className="tnum text-sm font-semibold">
        {v}% <span className="font-normal text-muted-foreground">completo</span>
      </div>
      <div className="h-1.5 w-[120px] overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${v}%` }} />
      </div>
    </div>
  )
}

interface NumberedSectionProps {
  index?: number
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  aside?: ReactNode
  children: ReactNode
  className?: string
}

export function NumberedSection({ icon, title, description, aside, children, className }: NumberedSectionProps) {
  return (
    <SectionCard title={title} icon={icon} description={description} action={aside} className={className}>
      {children}
    </SectionCard>
  )
}

export function StickyActionBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('sticky bottom-0 z-20 -mx-4 mt-6 border-t border-border bg-background/90 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6', className)}>
      <div className="flex items-center justify-end gap-2">{children}</div>
    </div>
  )
}

export { Check as SaveIcon }
